import React, { useState, useEffect, ChangeEvent } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { DefaultSwitch } from 'services/UIUtils';
import Icon, { IconSrc } from 'components/Utils/NewIcon';
import './style.scss';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import ConditionsAndActionsBuilder from '../ConditionsAndActionsBuilder';
import { HookConfiguration } from 'generated/api/models/HookConfiguration';
import { EventSchema } from 'generated/api/models/EventSchema';
import { HookCondition } from 'generated/api/models/HookCondition';
import { HookAction, HookActionTypeEnum } from 'generated/api/models/HookAction';
import { HookActionTypes } from '../Helpers';
import type {
  TeamsActionConfig,
  EmailActionConfig,
  PushActionConfig,
  SmsActionConfig,
} from '../Helpers';
import ConfirmationButton from '../../Utils/NewDefaultButton/ConfirmationButton';

interface HookDetailsProps {
  hook?: HookConfiguration | null;
  eventSchemas: EventSchema[];
  onSave?: (hook: HookConfiguration) => void;
  onDelete?: (hookId: string) => void;
  onCancel?: (hook: HookConfiguration) => void;
}

const HookDetails: React.FC<HookDetailsProps> = ({
  hook,
  eventSchemas,
  onSave,
  onDelete,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<HookConfiguration>({
    name: '',
    event_type: '',
    enabled: false,
    replaces_default: false,
    actions: [],
    conditions: [],
  });
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showBuilder, setShowBuilder] = useState<boolean>(false);

  // Initialize form data when hook changes
  useEffect(() => {
    if (hook) {
      setFormData({
        name: hook.name || '',
        event_type: hook.event_type || '',
        enabled: hook.enabled || false,
        replaces_default: hook.replaces_default || false,
        actions: hook.actions || [],
        conditions: hook.conditions || [],
      });
    } else {
      // Create mode - initialize with empty values
      setFormData({
        name: '',
        event_type: '',
        enabled: false,
        replaces_default: false,
        actions: [],
        conditions: [],
      });
    }
  }, [hook]);

  const handleInputChange = (field: keyof HookConfiguration, value: any): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (): Promise<void> => {
    if (!formData.name?.trim() || !formData.event_type) {
      return;
    }

    setSaving(true);
    try {
      let result: HookConfiguration | undefined;

      if (!hook) {
        // Create new hook (hook is null in create mode)
        const apiResult = await dispatch(
          api.Hooks.createHook({
            hookConfiguration: formData,
          }),
          {
            title: 'Create Hook',
            successText: 'Hook created successfully',
          },
        );
        result = apiResult as HookConfiguration;
      } else {
        // Update existing hook
        const apiResult = await dispatch(
          api.Hooks.updateHook({
            hookId: hook!.id!,
            hookConfiguration: formData,
          }),
          {
            title: 'Update Hook',
            successText: 'Hook updated successfully',
          },
        );
        result = apiResult as HookConfiguration;
      }

      if (result && onSave) {
        onSave(result);
      }
    } catch (error) {
      console.error('Failed to save hook:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (): void => {
    if (onCancel) {
      // Pass a dummy hook object for create mode, parent will handle based on isCreateMode
      onCancel(hook || ({} as HookConfiguration));
    }
  };

  const handleDelete = async (): Promise<void> => {
    // Can't delete in create mode
    if (!hook?.id) return;

    setDeleting(true);
    try {
      await dispatch(api.Hooks.deleteHook({ hookId: hook.id }), {
        errorTitle: 'Delete Hook',
        successText: 'Hook deleted successfully',
      });

      if (onDelete) {
        onDelete(hook.id);
      }
    } catch (error) {
      console.error('Failed to delete hook:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleEnabled = (): void => {
    handleInputChange('enabled', !formData.enabled);
  };

  const hasConfigurationChanged = (): boolean => {
    if (!hook) {
      // In create mode, consider changed if we have basic required fields
      return !!(formData.name?.trim() && formData.event_type);
    }

    // Compare current form data with original hook data
    return (
      formData.name !== hook.name ||
      formData.event_type !== hook.event_type ||
      formData.enabled !== hook.enabled ||
      (formData.replaces_default || false) !== (hook.replaces_default || false) ||
      JSON.stringify(formData.conditions) !== JSON.stringify(hook.conditions) ||
      JSON.stringify(formData.actions) !== JSON.stringify(hook.actions)
    );
  };

  const handleConditionsChange = (conditions: HookCondition[]) => {
    handleInputChange('conditions', conditions);
  };

  const handleActionsChange = (actions: HookAction[]) => {
    handleInputChange('actions', actions);
  };

  const getSelectedEventSchema = (): EventSchema | null => {
    if (!formData.event_type || !eventSchemas) return null;
    return eventSchemas.find((schema) => schema.event_type === formData.event_type) || null;
  };

  const getFieldDescription = (fieldName: string): string => {
    const eventSchema = getSelectedEventSchema();
    if (!eventSchema?.parameters) return fieldName;
    const parameter = eventSchema.parameters.find((p) => p.name === fieldName);
    return `${parameter?.category} ${parameter?.description || fieldName}`;
  };

  const formatConditionSummary = (condition: HookCondition): string => {
    const fieldDesc = getFieldDescription(condition.field);

    // Check if this is a field comparison operator
    const fieldOperators = [
      'equals_field',
      'not_equals_field',
      'contains_field',
      'not_contains_field',
      'greater_than_field',
      'less_than_field',
    ];
    const isFieldOperator = fieldOperators.includes(condition.operator);

    // Format operator text
    let operator = condition.operator.replace(/_/g, ' ');
    if (isFieldOperator) {
      operator = operator.replace(' field', ''); // Remove 'field' suffix for cleaner display
    }

    // Format value - if it's a field comparison, show the field description
    let value = '';
    if (condition.value) {
      if (isFieldOperator) {
        const compareFieldDesc = getFieldDescription(condition.value);
        value = ` ${compareFieldDesc}`;
      } else {
        value = ` "${condition.value}"`;
      }
    }

    return `${fieldDesc} ${operator}${value}`;
  };

  const formatActionSummary = (action: HookAction): string => {
    switch (action.type) {
      case HookActionTypeEnum.Email:
        const emailConfig = action.config as EmailActionConfig;
        const docAttachmentCount = emailConfig.attached_document_parameters?.length || 0;
        const generatedAttachmentCount = emailConfig.generated_attachments?.length || 0;
        const totalAttachments = docAttachmentCount + generatedAttachmentCount;
        const attachmentText =
          totalAttachments > 0
            ? ` (${totalAttachments} attachment${totalAttachments > 1 ? 's' : ''})`
            : '';
        return `Email to ${emailConfig.to || 'recipient'}${attachmentText}`;
      case HookActionTypeEnum.Sms:
        const smsConfig = action.config as SmsActionConfig;
        return `SMS to ${smsConfig.to || 'recipient'}`;
      case HookActionTypeEnum.Push:
        const pushConfig = action.config as PushActionConfig;
        return `Push to ${pushConfig.userRole || 'user'}`;
      case HookActionTypeEnum.Teams:
        const teamsConfig = action.config as TeamsActionConfig;
        return `Teams ${teamsConfig.channel ? `#${teamsConfig.channel}` : 'message'}`;
      default:
        return `${action.type}`;
    }
  };

  const getActionIcon = (action: HookAction): IconSrc | undefined => {
    return HookActionTypes.find((type) => type.value === action.type)?.icon;
  };

  const getGroupedEventSchemas = () => {
    const grouped: { [category: string]: EventSchema[] } = {};

    eventSchemas.forEach((schema) => {
      const category = schema.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category]!.push(schema);
    });

    return grouped;
  };

  const isNewHook = !hook; // Create mode when hook is null

  return (
    <div className="hook-details">
      <div className="hook-details-header">
        <h3>{isNewHook ? 'Create New Hook' : 'Edit Hook'}</h3>
        <div className="hook-actions">
          <DefaultSwitch
            label={formData.enabled ? 'Enabled' : 'Disabled'}
            checked={formData.enabled}
            onChange={handleToggleEnabled}
            disabled={isNewHook}
          />
          {!isNewHook && (
            <ConfirmationButton
              name="Delete"
              icon="trash"
              color="red"
              loading={deleting}
              onClick={handleDelete}
              style={{ marginLeft: '8px' }}
              confirmation={{
                message: 'Are you sure you want to delete this hook?',
              }}
            />
          )}
        </div>
      </div>

      <div className="hook-form">
        <div className="form-section">
          <div className="section-header">
            <h4>
              <Icon src="info-circle" />
              Basic Information
            </h4>
          </div>

          <div className="row gap-4">
            <DefaultSelectField
              label="Event Type"
              name="event_type"
              value={formData.event_type}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleInputChange('event_type', event.target.value);
              }}
              inputElements={(() => {
                const groupedSchemas = getGroupedEventSchemas();
                const elements: JSX.Element[] = [];

                Object.keys(groupedSchemas)
                  .sort()
                  .forEach((category) => {
                    elements.push(
                      <ListSubheader key={`header-${category}`} className="capitalize">
                        {category}
                      </ListSubheader>,
                    );

                    groupedSchemas[category]?.forEach((schema) => {
                      elements.push(
                        // Tooltip wraps the item content, not the MenuItem:
                        // Select reads `value` off its direct children.
                        <MenuItem key={schema.event_type} value={schema.event_type}>
                          <Tooltip title={schema.trigger_description || ''} placement="right" arrow>
                            <span style={{ display: 'block', width: '100%' }}>{schema.name}</span>
                          </Tooltip>
                        </MenuItem>,
                      );
                    });
                  });

                return elements;
              })()}
              placeholder="Select the event that triggers this hook"
              required
            />

            <DefaultTextField
              label="Hook Name"
              value={formData.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange('name', e.target.value)
              }
              placeholder="Enter a descriptive name for this hook"
              required
              style={{ width: 'auto', flexGrow: 1 }}
            />
          </div>

          {getSelectedEventSchema()?.trigger_description && (
            <p className="section-description" style={{ marginTop: 0 }}>
              <Icon src="info-circle" width="13px" />{' '}
              {getSelectedEventSchema()?.trigger_description}
            </p>
          )}

          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <DefaultSwitch
              label="Replaces default notification"
              checked={formData.replaces_default || false}
              onChange={() => handleInputChange('replaces_default', !formData.replaces_default)}
            />
            <p className="section-description" style={{ margin: 0 }}>
              When this hook matches an event, the system's built-in notification is suppressed on
              every channel this hook sends on (email/SMS/push). When it doesn't match, the built-in
              notification is sent as usual.
            </p>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h4>
              <Icon src="filter" />
              Conditions & Actions
            </h4>
            <IconButton
              onClick={() => setShowBuilder(true)}
              disabled={!formData.event_type}
              className="section-action-button"
              title="Configure Conditions & Actions"
            >
              <Icon src="pen-to-square" width="15px" />
            </IconButton>
          </div>

          <div className="combined-summary">
            {/* Conditions Summary */}
            {(formData.conditions?.length || 0) > 0 ? (
              <div className="conditions-summary">
                <p className="section-description">
                  {formData.conditions?.length || 0} condition(s) configured:
                </p>
                <div className="condition-chips">
                  {formData.conditions?.map((condition, index) => (
                    <span key={index} className="condition-chip">
                      {index > 0 && (
                        <span className="logical-op">
                          {(condition.logical_operator || 'and').toUpperCase()}
                        </span>
                      )}
                      <span className="condition-text">{formatConditionSummary(condition)}</span>
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="section-description">
                No conditions set - hook will trigger for all events of this type
              </p>
            )}

            {/* Actions Summary */}
            {formData.actions?.length > 0 ? (
              <div className="actions-summary">
                <p className="section-description">
                  {formData.actions.length} action(s) configured:
                </p>
                <div className="action-chips">
                  {formData.actions.map((action, index) => (
                    <span key={index} className="action-chip">
                      <Icon src={getActionIcon(action)!} height="15px" />{' '}
                      {formatActionSummary(action)}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="section-description">No actions configured - add at least one action</p>
            )}
          </div>
        </div>

        <div className="form-footer">
          {isNewHook && <DefaultButton name="Cancel" icon="times" onClick={handleCancel} />}
          <DefaultButton
            name={isNewHook ? 'Create Hook' : 'Save Changes'}
            icon="save"
            color="blue"
            loading={saving}
            onClick={handleSave}
            disabled={!formData.name?.trim() || !formData.event_type || !hasConfigurationChanged()}
          />
        </div>
      </div>

      {/* Conditions & Actions Builder Modal */}
      {showBuilder && (
        <ConditionsAndActionsBuilder
          key={'' + showBuilder}
          show={showBuilder}
          conditions={formData.conditions || []}
          actions={formData.actions || []}
          eventSchema={getSelectedEventSchema()}
          onConditionsChange={handleConditionsChange}
          onActionsChange={handleActionsChange}
          onClose={() => setShowBuilder(false)}
          hookId={hook?.id}
        />
      )}
    </div>
  );
};

export default HookDetails;
